// Apple Sign-in return bridge.
//
// Lifecycle:
//   1. User clicks "Continue with Apple" → /auth/apple/start → Apple authorize.
//   2. Apple POSTs the id_token to /api/auth/apple/callback (backend).
//   3. Backend verifies, creates/links user, sets session_token cookie,
//      issues a 303 redirect to /apple-return?ok=1 (or ?error=...).
//   4. This page reads `?ok=1` / `?error=...`, hydrates the auth context by
//      calling /auth/me, and navigates the user to wherever they were
//      headed (sessionStorage `bl_apple_next` or /dashboard).
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ERROR_LABELS = {
    state_mismatch: "Sign-in session expired. Please try again.",
    missing_nonce: "Sign-in session expired. Please try again.",
    missing_id_token: "Apple did not return a valid token. Please retry.",
    invalid_token: "Apple sign-in could not be verified. Please retry.",
    no_email: "Apple did not share an email. Use email/password or Google.",
    user_cancelled_authorize: "You cancelled Apple sign-in.",
};

export default function AppleReturn() {
    const [params] = useSearchParams();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [stage, setStage] = useState("loading");

    useEffect(() => {
        const error = params.get("error");
        if (error) {
            toast.error(ERROR_LABELS[error] || `Apple sign-in failed (${error})`);
            setStage("error");
            // Give the user a moment to read the toast before bouncing back.
            const t = setTimeout(() => navigate("/login", { replace: true }), 2200);
            return () => clearTimeout(t);
        }
        if (params.get("ok") !== "1") {
            navigate("/login", { replace: true });
            return undefined;
        }
        // Hydrate the auth context from /auth/me using the session cookie
        // the backend just set, then navigate where the user wanted to go.
        (async () => {
            try {
                await refresh();
                const next =
                    sessionStorage.getItem("bl_apple_next") || "/dashboard";
                try {
                    sessionStorage.removeItem("bl_apple_next");
                } catch {
                    /* ignore */
                }
                setStage("done");
                navigate(next, { replace: true });
            } catch {
                toast.error("Could not establish session — please log in again.");
                navigate("/login", { replace: true });
            }
        })();
        return undefined;
    }, [params, navigate, refresh]);

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-bl-bg"
            data-testid="apple-return-page"
        >
            <div className="text-center">
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                    Sign in with Apple
                </div>
                <div className="mt-2 font-display text-2xl text-bl">
                    {stage === "error" ? "Returning you to sign-in…" : "Signing you in…"}
                </div>
            </div>
        </div>
    );
}
